// src/lib/beds24-listings.js — Phase 2.E.7 (ADR-040)
// Helper client pour appeler l'endpoint backend de lecture d'annonce
// Beds24 (api/beds24-mapping.js?action=fetch_listing) et mapper les
// équipements vers le catalogue 30 items de SyncBeds24Tab.

import { supabase } from "../supabase"

// Récupère l'annonce publique côté Beds24 et la transforme vers le
// format SyncBeds24Tab. Lance une erreur si auth/network/404.
export async function fetchListingFromBeds24(propertyId, roomId = null) {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) throw new Error("Pas de session admin active")

  const r = await fetch('/api/beds24-mapping', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      action: 'fetch_listing',
      property_id: propertyId,
      room_id: roomId,
    }),
  })

  if (!r.ok) {
    let msg = `HTTP ${r.status}`
    try { const j = await r.json(); msg = j.error || msg } catch (_) {}
    throw new Error(`Beds24 fetch ${r.status}: ${msg}`)
  }
  const json = await r.json()

  // Mappe les tags équipements Beds24 → IDs catalogue FBZ V2.
  // Détaillé : on récupère aussi les codes non reconnus (hors bruit structurel)
  // pour les surfacer dans la modale d'import (angle mort historique : import
  // silencieux). Champ ADDITIF `equipements_ignored` — ne change pas la forme
  // existante de `listing` (equipements reste l'array d'IDs FBZ).
  const { mapped: equipements, ignored: equipements_ignored } =
    mapBeds24EquipementsDetailed(json.listing?.equipements_raw || [])
  return {
    ...json,
    listing: {
      ...json.listing,
      equipements, // IDs FBZ V2 directement utilisables par SyncBeds24Tab
      equipements_ignored, // codes Beds24 non reconnus (signal, hors bruit) — lecture seule UI
    },
  }
}

// ── Mapping équipements Beds24 → catalogue FBZ V2 (30 items) ──────
// Plusieurs tags Beds24 peuvent pointer vers le même ID FBZ V2.
// Tags input toujours en lowercase (normalisé côté backend, après .flat()
// du format `featureCodes: [['WIFI'], ['BEDROOM','BED_DOUBLE'], ...]`).
//
// Codes Beds24 V2 réels observés sur les rooms FBZ (capture live 2026-05-06,
// property 325559, 3 rooms : 675829/675830/675832) :
//   bathroom, bathroom_full, bathroom_half, bed_double, bedroom, blender,
//   cleaning_products, closet, co_detector, coffee, coffee_maker, dining_table,
//   dishes_utensils, dryer, fan_portable, freezer, hair_dryer, heating,
//   iron_board, kettle, kitchen, kitchenette, laptop_friendly, linens,
//   long_term_renters, microwave, oven, parking_possible, pets_not_allowed,
//   refrigerator, room_darkening_shades, shower_gel, smoke_detector, soap,
//   toaster, trash_compacter, tv, washer, water_hot, wifi
//
// Codes hérités (anciens fallbacks lowercase variantes Airbnb/Booking) conservés
// pour compat rétro — ne nuisent pas s'ils n'arrivent jamais.
const BEDS24_TO_FBZ = {
  // ── Codes Beds24 V2 réels (UPPER_SNAKE → lowercase) ─────────────
  // Essentiels
  'wifi': 'wifi',
  'heating': 'heating',
  // 'fan_portable' : ventilateur portable. ADR-060/ADR-189 — mappé vers 'fan'
  // (équipement à part entière), JAMAIS vers 'ac' (climatisation). Un voyageur
  // qui lit "Climatisation" sur Airbnb pour un simple ventilateur = risque
  // juridique + mauvais avis. Le mapping AC dédié reste 'air_conditioning'/'ac'
  // (codes hérités plus bas), distinct du ventilateur.
  'fan_portable': 'fan',
  'fan': 'fan', // variante lowercase legacy (cohérence collectFeatures)
  'laptop_friendly': 'workspace',

  // Cuisine
  'kitchen': 'kitchen',
  'kitchenette': 'kitchen',
  'coffee_maker': 'coffee',
  'coffee': 'coffee',
  // LOT ÉQUIPEMENTS (audit 2026-06-11) — codes Beds24 réels présents sur les
  // 3 rooms FBZ mais jusqu'ici PERDUS en silence à l'import. Désormais mappés.
  'microwave': 'microwave',
  'oven': 'oven',
  'refrigerator': 'fridge',
  'freezer': 'fridge', // freezer rattaché à fridge (pas d'id 'freezer' côté FBZ)
  'kettle': 'kettle',
  'toaster': 'toaster',

  // Linge
  'washer': 'washer',
  'dryer': 'dryer',
  'iron_board': 'iron',

  // Loisirs
  'tv': 'tv',

  // Stationnement
  'parking_possible': 'parking_street',  // Beds24 PARKING_POSSIBLE = parking gratuit dispo dans la rue (le plus proche du sens)

  // Politique animaux (Beds24 V2 expose la NÉGATION : si PETS_NOT_ALLOWED présent
  // → ne pas cocher 'pets'. Pas de mapping ici, géré par absence)

  // Salle de bain
  'hair_dryer': 'hairdryer',
  'shower_gel': 'amenities',
  'soap': 'amenities',

  // Sécurité
  'smoke_detector': 'smoke_detector',
  'co_detector': 'co_detector',

  // Note : codes Beds24 sans équivalent FBZ V2 — laissés non mappés
  // (apparaîtront en equipements_raw côté UI, sans coche FBZ). La plupart sont
  // du bruit structurel filtré par BEDS24_STRUCTURAL_NOISE (cf. plus bas) :
  //   bathroom, bathroom_full, bathroom_half, bedroom, bed_double, blender,
  //   cleaning_products, closet, dining_table, dishes_utensils,
  //   linens, long_term_renters, room_darkening_shades, trash_compacter,
  //   water_hot, pets_not_allowed

  // ── Codes hérités (Airbnb/Booking lowercase variantes) ──────────
  // Essentiels
  'internet': 'wifi', 'wireless': 'wifi', 'wireless_internet': 'wifi',
  'air_conditioning': 'ac', 'ac': 'ac', 'aircon': 'ac', 'climatisation': 'ac',
  'central_heating': 'heating', 'chauffage': 'heating',
  'workspace': 'workspace', 'desk': 'workspace', 'office': 'workspace', 'laptop_friendly_workspace': 'workspace',

  // Cuisine
  'fully_equipped_kitchen': 'kitchen', 'cuisine': 'kitchen',
  'dishwasher': 'dishwasher', 'lave_vaisselle': 'dishwasher',
  'coffee_machine': 'coffee', 'espresso': 'coffee',
  'breakfast': 'breakfast', 'breakfast_included': 'breakfast', 'petit_dejeuner': 'breakfast',

  // Linge
  'washing_machine': 'washer', 'laundry': 'washer', 'lave_linge': 'washer',
  'tumble_dryer': 'dryer', 'seche_linge': 'dryer',
  'iron': 'iron', 'ironing_board': 'iron', 'fer_a_repasser': 'iron',

  // Loisirs
  'television': 'tv', 'cable_tv': 'tv',
  'smart_tv': 'streaming', 'netflix': 'streaming', 'streaming': 'streaming',

  // Extérieur
  'pool': 'pool', 'swimming_pool': 'pool', 'piscine': 'pool',
  'balcony': 'balcony', 'terrace': 'balcony', 'terrasse': 'balcony',
  'garden': 'garden', 'yard': 'garden', 'jardin': 'garden',

  // Stationnement
  'free_parking_on_premises': 'parking_free', 'parking_on_premises': 'parking_free', 'parking_free': 'parking_free', 'free_parking': 'parking_free',
  'street_parking': 'parking_street', 'free_street_parking': 'parking_street', 'parking_street': 'parking_street',
  'paid_parking': 'parking_paid', 'parking_paid': 'parking_paid', 'paid_parking_on_premises': 'parking_paid', 'garage_paid': 'parking_paid',

  // Bâtiment
  'elevator': 'elevator', 'lift': 'elevator', 'ascenseur': 'elevator',

  // Politique
  'pets_allowed': 'pets', 'pet_friendly': 'pets', 'pets': 'pets', 'animaux': 'pets',
  'family_friendly': 'family', 'kid_friendly': 'family', 'family': 'family', 'familles': 'family',

  // Famille
  'crib': 'crib', 'baby_bed': 'crib', 'cot': 'crib', 'lit_bebe': 'crib',
  'high_chair': 'high_chair', 'highchair': 'high_chair', 'baby_chair': 'high_chair', 'chaise_haute': 'high_chair',

  // Salle de bain
  'hairdryer': 'hairdryer', 'seche_cheveux': 'hairdryer',
  'amenities': 'amenities', 'toiletries': 'amenities', 'bath_amenities': 'amenities', 'shampoo': 'amenities',

  // Sécurité
  'smoke_alarm': 'smoke_detector', 'detecteur_fumee': 'smoke_detector',
  'carbon_monoxide_detector': 'co_detector', 'co_alarm': 'co_detector',
  'first_aid': 'first_aid', 'first_aid_kit': 'first_aid', 'trousse_secours': 'first_aid',
  'extinguisher': 'extinguisher', 'fire_extinguisher': 'extinguisher', 'extincteur': 'extinguisher',
}

// ── Bruit structurel Beds24 (audit 2026-06-11) ────────────────────
// Codes Beds24 qui décrivent la STRUCTURE/le mobilier d'un logement plutôt
// qu'un équipement « signal » à présenter au voyageur. On les exclut de la
// liste des `ignored` pour que l'UI d'import n'avertisse QUE sur du vrai
// signal non reconnu (ex. un nouveau code équipement non mappé), et pas sur
// du bruit attendu. Tous en lowercase (les tags sont normalisés lowercase).
export const BEDS24_STRUCTURAL_NOISE = [
  'bedroom',
  'bed_double',
  'closet',
  'bathroom',
  'bathroom_full',
  'bathroom_half',
  'dining_table',
  'dishes_utensils',
  'linens',
  'long_term_renters',
  'pets_not_allowed',
  'room_darkening_shades',
  'trash_compacter',
  'water_hot',
  'cleaning_products',
  'soap',
  'blender',
]

// Set lowercase pour lookup O(1) du bruit structurel.
const STRUCTURAL_NOISE_SET = new Set(BEDS24_STRUCTURAL_NOISE)

// Mapping détaillé : retourne à la fois les IDs FBZ mappés ET les codes Beds24
// non reconnus (hors bruit structurel). Permet à l'UI d'import d'avertir
// l'admin quand un signal réel est perdu (angle mort historique : l'import
// ignorait en SILENCE). N'EXCLUT PAS le mapping existant — `mapBeds24Equipements`
// reste un wrapper de cette fonction (compat API stricte).
export function mapBeds24EquipementsDetailed(beds24Tags) {
  const mapped = new Set()
  const ignored = []
  const ignoredSeen = new Set()
  for (const tag of (beds24Tags || [])) {
    if (typeof tag !== 'string') continue
    const norm = tag.toLowerCase().trim()
    if (!norm) continue
    const id = BEDS24_TO_FBZ[norm] || null
    if (id) {
      mapped.add(id)
      continue
    }
    // Non mappé : on l'ignore — mais on ne le remonte comme « non reconnu »
    // que si ce n'est PAS du bruit structurel attendu (dédup conservé).
    if (STRUCTURAL_NOISE_SET.has(norm)) continue
    if (ignoredSeen.has(norm)) continue
    ignoredSeen.add(norm)
    ignored.push(norm)
  }
  return { mapped: Array.from(mapped), ignored }
}

export function mapBeds24Equipements(beds24Tags) {
  return mapBeds24EquipementsDetailed(beds24Tags).mapped
}

// WAGON R (Lot R4 d) — `pushListingToBeds24` SUPPRIMÉ : le push de contenu vers
// Beds24 est abandonné (l'onglet Sync Beds24 et la modale d'export ont été
// retirés ; la gestion du contenu se fait directement sur les plateformes).
// `fetchListingFromBeds24` (import lecture seule) et `valuesDiffer` (diff de la
// modale d'import) RESTENT — encore consommés par l'onglet Website.

// Helper de comparaison utilisé par ImportPreviewModal pour décider si
// un champ a une "vraie" différence à proposer (sinon on le pré-décoche).
export function valuesDiffer(current, fetched) {
  if (current === fetched) return false
  // Both null/undefined/empty string treated as "vide"
  const isEmpty = (v) => v === null || v === undefined || v === ''
  if (isEmpty(current) && isEmpty(fetched)) return false
  // Arrays : comparer comme set (pour équipements)
  if (Array.isArray(current) && Array.isArray(fetched)) {
    if (current.length !== fetched.length) return true
    const sa = [...current].sort().join('|')
    const sb = [...fetched].sort().join('|')
    return sa !== sb
  }
  // Objects : compare via JSON (cas capacity / pricing / schedule / public_address)
  if (typeof current === 'object' && typeof fetched === 'object' && current && fetched) {
    return JSON.stringify(current) !== JSON.stringify(fetched)
  }
  return String(current) !== String(fetched)
}
